import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@shared/utils";

type ButtonVariant = "primary" | "secondary" | "outline" | "ghost";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  children: ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: `
    bg-[var(--color-secondary-120)]
    text-[var(--color-primary-110)]
    hover:bg-[var(--color-secondary-50)]
  `,
  secondary: `
    bg-secondary-60 text-white hover:bg-secondary-70
  `,
  outline: `
    border border-primary text-primary hover:bg-primary-10
  `,
  ghost: `
    text-primary hover:bg-primary-10
  `,
};

/**
 * Base Button atom.
 * Supports variant customization via Tailwind tokens.
 */
export function Button({
  variant = "primary",
  className,
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        `
        inline-flex items-center justify-center
        rounded-full
        px-[45px] py-[12px]
        h-[40px]
        text-[14px]
        font-semibold
        cursor-pointer
        transition-colors
        focus:outline-none
        disabled:opacity-50 disabled:cursor-not-allowed
        `,
        variantStyles[variant],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}
